import styled from 'styled-components';

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: color-mix(in srgb, var(--brand-navy, #00212d) 52%, transparent);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 1rem;
`;

export const ModalContent = styled.div`
  position: relative;
  background: var(--surface-1);
  border: 1px solid var(--border-subtle);
  border-radius: 18px;
  padding: 2rem 1.75rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: var(--shadow-lg);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--brand-cta-gradient-135);
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 0.85rem;
  right: 0.85rem;
  background: var(--surface-3);
  border: none;
  color: var(--text-secondary);
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: bold;
  transition: all 0.2s ease;
  z-index: 2;

  &:hover {
    background: var(--surface-hover);
    color: var(--text-heading);
  }
`;

export const ModalTitle = styled.h2`
  color: var(--text-heading);
  font-size: 1.45rem;
  margin: 0.25rem 0 1.5rem;
  text-align: left;
  font-weight: 700;
  line-height: 1.3;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const Label = styled.label`
  color: var(--text-secondary);
  font-weight: 600;
  font-size: 1rem;
  text-shadow: 1px 1px 2px var(--surface-overlay);
`;

export const Input = styled.input`
  background: var(--input-bg);
  border: 2px solid var(--border-default);
  border-radius: 12px;
  padding: 1rem;
  color: var(--text-primary);
  font-size: 1rem;
  transition: all 0.3s ease;
  outline: none;

  &::placeholder {
    color: var(--input-placeholder);
  }

  &:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-soft);
    background: var(--input-bg-focus);
  }

  &:disabled {
    background: var(--surface-2);
    color: var(--text-muted);
    cursor: not-allowed;
  }

  &.error {
    border-color: var(--danger);
    box-shadow: 0 0 0 3px var(--danger-soft);
  }
`;

export const Select = styled.select`
  background: var(--input-bg);
  border: 2px solid var(--border-default);
  border-radius: 12px;
  padding: 1rem;
  color: var(--text-primary);
  font-size: 1rem;
  transition: all 0.3s ease;
  outline: none;
  cursor: pointer;

  option {
    background: var(--surface-1);
    color: var(--text-primary);
    padding: 0.5rem;
  }

  &:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-soft);
    background: var(--input-bg-focus);
  }

  &:disabled {
    background: var(--surface-2);
    color: var(--text-muted);
    cursor: not-allowed;
  }

  &.error {
    border-color: var(--danger);
    box-shadow: 0 0 0 3px var(--danger-soft);
  }
`;

export const PackageInfo = styled.div`
  background: var(--accent-soft);
  border: 2px solid var(--accent);
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
  margin-bottom: 0.5rem;
`;

export const PackageName = styled.div`
  color: var(--accent);
  font-weight: 700;
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
`;

export const PackageLabel = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
  opacity: 0.9;
`;

export const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

/* CTA principal mantém o gradiente de marca (verde/azul Vert) em ambos os temas. */
export const SubmitButton = styled.button`
  background: var(--brand-cta-gradient);
  color: var(--accent-secondary);
  border: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  flex: 1;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: var(--shadow-md);

  &:hover {
    background: var(--brand-cta-gradient-hover);
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: var(--surface-3);
    color: var(--text-muted);
    cursor: not-allowed;
    transform: none;
  }
`;

export const CancelButton = styled.button`
  background: var(--danger-soft);
  color: var(--danger);
  border: 2px solid var(--danger);
  padding: 1rem 2rem;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  flex: 1;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;

  &:hover {
    background: color-mix(in srgb, var(--danger) 30%, transparent);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const SuccessMessage = styled.div`
  color: var(--success);
  text-align: center;
  padding: 1rem;
  background: var(--accent-soft);
  border-radius: 8px;
  border: 1px solid var(--accent);
  margin-bottom: 1rem;
  font-weight: 500;
`;

export const ErrorMessage = styled.div`
  color: var(--danger);
  text-align: center;
  padding: 1rem;
  background: color-mix(in srgb, var(--danger) 12%, transparent);
  border-radius: 8px;
  border: 1px solid var(--danger);
  margin-bottom: 1rem;
  font-weight: 500;
`;

export const FieldError = styled.div`
  color: var(--danger);
  font-size: 0.8rem;
  margin-top: 0.25rem;
  font-weight: 500;
  text-shadow: 1px 1px 2px var(--surface-overlay);
`;
